import { useMemo, useState } from 'react';
import {
  Area,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { ChartTooltipV2 } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { ChartContainer } from '@Components/ChartContainer';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { Neutral300, Neutral400, Neutral500, Neutral600 } from '@src/utils/colors';
import { HierarchyLevelConst, SaturationRow } from '../../dtc-commerce-mmm-types';
import { formatCurrency, formatWholeNumber } from '../../utils/dtc-commerce-mmm-format';
import { ChannelTooltipSubheader } from '../ChannelTooltipSubheader';
import { EXPANDED_CHART_HEIGHT, ExpandableChartName } from './channel-drawer-constants';
import { DownloadActions, SatChannelData } from './channel-drawer-types';
import { axisFontSizeFor } from './channel-drawer-utils';
import { ChannelDrawerExpandableChart } from './ChannelDrawerExpandableChart';

const SATURATION_HEIGHT = 220;

interface ChannelDrawerSaturationProps {
  data: SatChannelData[];
  focusedNames: Set<string> | null;
  isRevenue: boolean;
  dateRangeLabel: string;
  focusedLabel?: string;
  channelColors: Map<string, string>;
  ciPct: number | null;
  download: DownloadActions;
}

interface MergedRow {
  effectiveAdstockSpend: number;
  ciBand?: [number, number];
  [key: string]: number | [number, number] | undefined;
}

interface ChannelKey {
  name: string;
  label: string;
  responseKey: string;
  channel: string;
  platform: string;
  mediaType: string;
  rows: SaturationRow[];
  meanSpend: number | null;
  meanResponse: number | null;
  meanMarginal: number | null;
}

interface TooltipPayloadItem {
  value?: number;
  dataKey?: string;
  color?: string;
  payload?: MergedRow;
}

interface SaturationTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  channelKeys: ChannelKey[];
  focusedNames: Set<string> | null;
  isRevenue: boolean;
}

interface ChannelMetricsCardProps {
  channelKey: ChannelKey;
  spend: number;
  response: number;
  isRevenue: boolean;
  color: string;
}

const computeEfficiency = (spend: number, response: number, isRevenue: boolean): number | null => {
  if (response <= 0 || spend <= 0) return null;
  return isRevenue ? response / spend : spend / response;
};

const computeMarginalDollars = (marginal: number | null, isRevenue: boolean): number | null => {
  if (marginal === null) return null;
  if (isRevenue) return marginal;
  if (marginal <= 0) return null;
  return 1 / marginal;
};

const ChannelMetricsCard = ({
  channelKey,
  spend,
  response,
  isRevenue,
  color
}: ChannelMetricsCardProps) => {
  const fmtResponse = isRevenue ? formatCurrency : formatWholeNumber;
  const efficiencyAtPoint = computeEfficiency(spend, response, isRevenue);
  const marginalAtPoint = interpMarginal(channelKey.rows, spend);
  const marginalDollars = computeMarginalDollars(marginalAtPoint, isRevenue);
  const responseLabel = isRevenue ? 'Revenue' : 'Conversions';
  // A point on the model's curve, not what actually happened, so it reads as modeled to keep it
  // apart from the drawer header's actual ROAS.
  const efficiencyLabel = isRevenue ? 'Modeled ROAS' : 'Modeled CPA';
  const marginalLabel = isRevenue ? 'Next Dollar ROAS' : 'Next Conversion CPA';
  // Only mark the corner dot when the hover X actually lands on this channel's average
  // weekly spend — i.e., the user is hovering the reference dot itself.
  const isAtCurrentSpend = channelKey.meanSpend !== null && channelKey.meanSpend === spend;
  const headerLabel = isAtCurrentSpend ? `${channelKey.label} · ●  avg weekly` : channelKey.label;

  return (
    <ChartTooltipV2
      headers={[headerLabel]}
      subHeaderLabel={
        <ChannelTooltipSubheader
          channel={channelKey.channel}
          platform={channelKey.platform}
          mediaType={channelKey.mediaType}
          hierarchyLevel={HierarchyLevelConst.PLATFORM}
        />
      }
      items={[
        {
          label: 'Weekly spend',
          value: formatCurrency(spend),
          color
        },
        {
          label: responseLabel,
          value: fmtResponse(response),
          color
        },
        {
          label: efficiencyLabel,
          value: efficiencyAtPoint === null ? '—' : `$${efficiencyAtPoint.toFixed(2)}`,
          color: 'transparent'
        },
        {
          label: marginalLabel,
          value: marginalDollars === null ? '—' : `$${marginalDollars.toFixed(2)}`,
          color: 'transparent'
        }
      ]}
    />
  );
};

const SaturationTooltip = ({
  active,
  payload,
  channelKeys,
  focusedNames,
  isRevenue
}: SaturationTooltipProps) => {
  if (!active || !payload?.length) return null;
  const spend = payload[0]?.payload?.effectiveAdstockSpend;
  if (spend === undefined) return null;

  const singleFocus = focusedNames !== null && focusedNames.size === 1;
  let entries = payload.filter(e => typeof e.value === 'number');
  if (singleFocus && focusedNames !== null) {
    entries = entries.filter(e => {
      const ck = channelKeys.find(k => k.responseKey === e.dataKey);
      return ck !== undefined && focusedNames.has(ck.name);
    });
  }
  if (entries.length === 0) return null;

  const cards = entries
    .map(entry => {
      const ck = channelKeys.find(k => k.responseKey === entry.dataKey);
      if (!ck || typeof entry.value !== 'number') return null;
      return { ck, response: entry.value, color: entry.color ?? Neutral300 };
    })
    .filter((c): c is { ck: ChannelKey; response: number; color: string } => c !== null);

  if (cards.length === 0) return null;

  return (
    <div className='channel-drawer-curve-tooltip-stack'>
      {cards.map(({ ck, response, color }) => (
        <ChannelMetricsCard
          key={ck.name}
          channelKey={ck}
          spend={spend}
          response={response}
          isRevenue={isRevenue}
          color={color}
        />
      ))}
    </div>
  );
};

const lerpField = (
  rows: SaturationRow[],
  x: number,
  field: 'effectiveResponse' | 'ciLoResponse' | 'ciHiResponse'
): number | null => {
  if (rows.length === 0) return null;
  if (x < rows[0].effectiveAdstockSpend || x > rows[rows.length - 1].effectiveAdstockSpend) {
    return null;
  }
  let lo = 0;
  let hi = rows.length - 1;
  while (lo < hi - 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (rows[mid].effectiveAdstockSpend <= x) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  const x0 = rows[lo].effectiveAdstockSpend;
  const x1 = rows[hi].effectiveAdstockSpend;
  const t = x1 === x0 ? 0 : (x - x0) / (x1 - x0);
  return rows[lo][field] + t * (rows[hi][field] - rows[lo][field]);
};

const lerp = (rows: SaturationRow[], x: number): number | null =>
  lerpField(rows, x, 'effectiveResponse');

// Like lerp(effectiveResponse) but always returns a value: extrapolates below the first
// sample using the first point's slope (clamped at 0) and above the last sample using the
// last point's slope. Used for the "current spend" dot so it renders even when the channel's
// current spend is outside the model's observed range.
const extrapResponse = (rows: SaturationRow[], x: number): number | null => {
  if (rows.length === 0) return null;
  if (x <= 0) return 0;
  const first = rows[0];
  if (x <= first.effectiveAdstockSpend) {
    return Math.max(
      0,
      first.effectiveResponse - (first.effectiveAdstockSpend - x) * first.effectiveReturnNextDollar
    );
  }
  const inside = lerpField(rows, x, 'effectiveResponse');
  if (inside !== null) return inside;
  const last = rows[rows.length - 1];
  return last.effectiveResponse + (x - last.effectiveAdstockSpend) * last.effectiveReturnNextDollar;
};

// Interpolates the marginal return (next-dollar) at any spend along the curve. Clamps to
// the endpoint marginals outside the observed range.
const interpMarginal = (rows: SaturationRow[], x: number): number | null => {
  if (rows.length === 0) return null;
  if (x <= rows[0].effectiveAdstockSpend) return rows[0].effectiveReturnNextDollar;
  if (x >= rows[rows.length - 1].effectiveAdstockSpend) {
    return rows[rows.length - 1].effectiveReturnNextDollar;
  }
  let lo = 0;
  let hi = rows.length - 1;
  while (lo < hi - 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (rows[mid].effectiveAdstockSpend <= x) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  const x0 = rows[lo].effectiveAdstockSpend;
  const x1 = rows[hi].effectiveAdstockSpend;
  const t = x1 === x0 ? 0 : (x - x0) / (x1 - x0);
  return (
    rows[lo].effectiveReturnNextDollar +
    t * (rows[hi].effectiveReturnNextDollar - rows[lo].effectiveReturnNextDollar)
  );
};

export const ChannelDrawerSaturation = ({
  data,
  focusedNames,
  isRevenue,
  dateRangeLabel,
  focusedLabel,
  channelColors,
  ciPct,
  download
}: ChannelDrawerSaturationProps) => {
  const [showCI, setShowCI] = useState(false);
  const colorFor = (name: string): string => channelColors.get(name) ?? Neutral300;
  const hasFocus = focusedNames !== null && focusedNames.size > 0;

  const singleSubName: string | null = (() => {
    if (data.length === 1) return data[0].name;
    if (hasFocus && focusedNames !== null && focusedNames.size === 1) {
      return Array.from(focusedNames)[0];
    }
    return null;
  })();
  const ciEnabled = singleSubName !== null;
  const showBand = showCI && ciEnabled;

  const { mergedData, channelKeys } = useMemo(() => {
    if (data.length === 0) return { mergedData: [], channelKeys: [] };

    const keys: ChannelKey[] = data.map((ch, i) => {
      const meanSpend = ch.weeklyAvgSpend;
      const meanResponse = meanSpend !== null ? extrapResponse(ch.rows, meanSpend) : null;
      const meanMarginal = meanSpend !== null ? interpMarginal(ch.rows, meanSpend) : null;
      return {
        name: ch.name,
        label: ch.label,
        responseKey: `r${i}`,
        channel: ch.channel,
        platform: ch.platform,
        mediaType: ch.mediaType,
        rows: ch.rows,
        meanSpend,
        meanResponse,
        meanMarginal
      };
    });

    const ciChannel = singleSubName !== null ? data.find(ch => ch.name === singleSubName) : null;

    // Include each channel's weekly-avg spend as an X point in the merged data so the line
    // actually passes through the "current spend" coordinate. That makes the hovered active
    // dot land exactly on the reference dot, and the regular Tooltip can fire at that X.
    const xSet = new Set<number>();
    data.forEach(ch => ch.rows.forEach(r => xSet.add(r.effectiveAdstockSpend)));
    data.forEach(ch => {
      if (ch.weeklyAvgSpend !== null) {
        xSet.add(ch.weeklyAvgSpend);
      }
    });
    const sortedX = Array.from(xSet).sort((a, b) => a - b);

    const merged: MergedRow[] = sortedX.map(x => {
      const row: MergedRow = { effectiveAdstockSpend: x };
      data.forEach((ch, i) => {
        // For a channel's own current-spend X, extrapolate so the line still passes through
        // even when the spend is outside the model's observed range.
        const v = ch.weeklyAvgSpend === x ? extrapResponse(ch.rows, x) : lerp(ch.rows, x);
        if (v !== null) {
          row[keys[i].responseKey] = v;
        }
      });
      if (ciChannel) {
        const lo = lerpField(ciChannel.rows, x, 'ciLoResponse');
        const hi = lerpField(ciChannel.rows, x, 'ciHiResponse');
        if (lo !== null && hi !== null) {
          row.ciBand = [lo, hi];
        }
      }
      return row;
    });

    return { mergedData: merged, channelKeys: keys };
  }, [data, singleSubName]);

  // Lock Y-axis to the line responses (plus extrapolated dot Ys) so toggling the CI band
  // on/off doesn't resize the chart. Including extrapolated dot Ys ensures the dot stays
  // visible when current spend is above the model's observed range.
  const lineYDomain = useMemo<[number, number | 'auto']>(() => {
    const shown = hasFocus ? data.filter(ch => focusedNames?.has(ch.name)) : data;
    let maxResponse = shown.reduce(
      (m, ch) => ch.rows.reduce((mm, r) => Math.max(mm, r.effectiveResponse), m),
      0
    );
    shown.forEach(ch => {
      if (ch.weeklyAvgSpend === null) return;
      const extrap = extrapResponse(ch.rows, ch.weeklyAvgSpend);
      if (extrap !== null) {
        maxResponse = Math.max(maxResponse, extrap);
      }
    });
    if (maxResponse <= 0) return [0, 'auto'];
    return [0, maxResponse];
  }, [data, focusedNames, hasFocus]);

  if (data.length === 0) return null;

  const fmtResponse = isRevenue ? formatCurrency : formatWholeNumber;
  const subtitle = focusedLabel
    ? `Showing ${focusedLabel}`
    : 'Diminishing returns at different spend levels';
  const ciToggleLabel =
    ciPct !== null ? `Show ${ciPct}% credible intervals` : 'Show credible intervals';
  const ciBandColor = singleSubName !== null ? colorFor(singleSubName) : Neutral300;

  return (
    <ChannelDrawerExpandableChart chartName={ExpandableChartName.SATURATION}>
      {(isExpanded, expandButton) => (
        <ChartContainer
          className='channel-drawer-saturation'
          title={
            <div className='dtc-commerce-mmm-title-stack'>
              <div>Saturation</div>
              <div className='dtc-commerce-mmm-title-stack-sub'>{subtitle}</div>
            </div>
          }
          viewRightActions
          rightActions={
            <div className='channel-drawer-section-right-actions'>
              <ToggleSwitch
                label={ciToggleLabel}
                checked={showCI}
                onChange={setShowCI}
                disabled={!ciEnabled}
              />
              <DownloadDropdown
                onClickOptions={[download.onExcel, () => download.onPng(isExpanded)]}
              />
              {expandButton}
            </div>
          }
        >
          <div className='channel-drawer-chart-stack'>
            <ResponsiveContainer
              width='100%'
              height={isExpanded ? EXPANDED_CHART_HEIGHT : SATURATION_HEIGHT}
            >
              <ComposedChart data={mergedData} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
                <XAxis
                  dataKey='effectiveAdstockSpend'
                  type='number'
                  tickFormatter={v => formatCurrency(v)}
                  tick={{ fontSize: axisFontSizeFor(isExpanded), fill: Neutral600 }}
                  axisLine={{ stroke: Neutral400 }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={v => fmtResponse(v)}
                  tick={{ fontSize: axisFontSizeFor(isExpanded), fill: Neutral600 }}
                  width={64}
                  axisLine={{ stroke: Neutral400 }}
                  tickLine={false}
                  domain={lineYDomain}
                  allowDataOverflow
                />
                {singleSubName !== null && (
                  <Tooltip
                    content={
                      <SaturationTooltip
                        channelKeys={channelKeys}
                        focusedNames={focusedNames}
                        isRevenue={isRevenue}
                      />
                    }
                    cursor={{ stroke: Neutral500, strokeWidth: 1, strokeDasharray: '4 3' }}
                  />
                )}
                {showBand && (
                  <Area
                    type='monotone'
                    dataKey='ciBand'
                    stroke='none'
                    fill={ciBandColor}
                    fillOpacity={0.18}
                    isAnimationActive={false}
                    activeDot={false}
                  />
                )}
                {channelKeys.map(ck => {
                  const isFocused =
                    !hasFocus || (focusedNames !== null && focusedNames.has(ck.name));
                  if (hasFocus && !isFocused) return null;
                  const color = colorFor(ck.name);
                  return (
                    <Line
                      key={ck.name}
                      type='monotone'
                      dataKey={ck.responseKey}
                      stroke={color}
                      strokeWidth={hasFocus && isFocused ? 2.5 : 2}
                      dot={false}
                      connectNulls={false}
                      name={ck.name}
                      isAnimationActive={false}
                      activeDot={
                        singleSubName !== null
                          ? { r: 5, fill: color, stroke: 'white', strokeWidth: 2 }
                          : false
                      }
                    />
                  );
                })}
                {channelKeys.map(ck => {
                  if (ck.meanSpend === null || ck.meanResponse === null) return null;
                  const isFocused =
                    !hasFocus || (focusedNames !== null && focusedNames.has(ck.name));
                  if (hasFocus && !isFocused) return null;
                  return (
                    <ReferenceDot
                      key={`mean-${ck.name}`}
                      x={ck.meanSpend}
                      y={ck.meanResponse}
                      r={5}
                      fill={colorFor(ck.name)}
                      stroke='white'
                      strokeWidth={2}
                      ifOverflow='extendDomain'
                    />
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>
            {data.length > 1 && (
              <div className='channel-drawer-legend'>
                {data.map(ch => {
                  const isFocused = !hasFocus || focusedNames?.has(ch.name);
                  return (
                    <div
                      key={ch.name}
                      className='channel-drawer-legend-item'
                      style={{ opacity: hasFocus && !isFocused ? 0.4 : 1 }}
                    >
                      <span
                        className='channel-drawer-legend-swatch'
                        style={{
                          backgroundColor: hasFocus && !isFocused ? Neutral300 : colorFor(ch.name)
                        }}
                      />
                      {ch.label}
                    </div>
                  );
                })}
              </div>
            )}
            {data.some(ch => ch.weeklyAvgSpend !== null) && (
              <div className='channel-drawer-card-footnote'>
                ● = average weekly spend
                {dateRangeLabel ? ` in ${dateRangeLabel.toLowerCase()}` : ''}
              </div>
            )}
          </div>
        </ChartContainer>
      )}
    </ChannelDrawerExpandableChart>
  );
};
